import {
  CHANNEL_KEYS,
  OPTIONAL_CHANNEL_KEYS
} from "../constants/notifications-settings"
import { type SmsLock } from "../sms/use-sms-verification"
import type {
  NotificationSettings,
  NotificationSettingsItem
} from "../types/notification.types"

export type Channel =
  | "email"
  | "in_app"
  | "phone"
  | "desktop"
  | "whatsapp"
  | "sms"
export type ChannelValues = Partial<Record<Channel, boolean>>
export type DraftOverrides = Record<number, ChannelValues>
export type RowValues = Record<number, ChannelValues>

export const CHANNELS = [...CHANNEL_KEYS, ...OPTIONAL_CHANNEL_KEYS] as Channel[]

// When a bulk "off" would empty a critical row, one channel is kept on. This is
// the order it prefers among the channels currently enabled on that row.
const KEEP_PRIORITY: Channel[] = [
  "in_app",
  "email",
  "phone",
  "desktop",
  "whatsapp",
  "sms"
]

// The channels this row can carry. A row only gets a control for a channel the
// server sent, so a payload from before WhatsApp or SMS saves exactly as before,
// and SMS only where the server says the type may go by text.
export function rowChannels(item: NotificationSettingsItem): Channel[] {
  return CHANNELS.filter((c) => {
    if (c === "sms") return item.sms_available === true
    if (c === "whatsapp") return typeof item.whatsapp === "boolean"
    return true
  })
}

// A locked SMS control keeps the stored choice; bulk actions pass over it.
export function editableChannels(
  item: NotificationSettingsItem,
  smsLock: SmsLock
): Channel[] {
  return rowChannels(item).filter((c) => c !== "sms" || !smsLock)
}

export function groupChannels(items: NotificationSettingsItem[]): Channel[] {
  return CHANNELS.filter((c) => items.some((it) => rowChannels(it).includes(c)))
}

export function enabledCount(row: ChannelValues, channels: Channel[]) {
  return channels.filter((c) => row[c]).length
}

// Value a single channel takes when a bulk "off" sweeps it: false, unless that
// would leave a critical row with nothing on — then it stays.
function clearedChannel(
  row: ChannelValues,
  channel: Channel,
  channels: Channel[],
  isCriticalGroup: boolean
): boolean {
  if (isCriticalGroup && row[channel] && enabledCount(row, channels) === 1)
    return true
  return false
}

// Whole-row value after a bulk "off". A critical row keeps its highest-priority
// still-enabled channel so finance alerts never go fully dark.
function clearedRow(
  row: ChannelValues,
  channels: Channel[],
  isCriticalGroup: boolean
): ChannelValues {
  if (!isCriticalGroup || enabledCount(row, channels) === 0) {
    return Object.fromEntries(channels.map((c) => [c, false])) as ChannelValues
  }
  const keep =
    KEEP_PRIORITY.find((c) => channels.includes(c) && row[c]) ?? "in_app"
  return Object.fromEntries(
    channels.map((c) => [c, c === keep])
  ) as ChannelValues
}

// One row per notification type. A type listed under two categories (Customer
// Activity shares its types with Projects, Files and Calendar) is one saved
// record, so it is one value here too.
export function savedRows(settings: NotificationSettings): RowValues {
  const rows: RowValues = {}
  Object.values(settings)
    .flat()
    .forEach((item) => {
      if (rows[item.notification_type]) return
      rows[item.notification_type] = Object.fromEntries(
        rowChannels(item).map((c) => [c, item[c]])
      ) as ChannelValues
    })
  return rows
}

export function currentRow(
  item: NotificationSettingsItem,
  overrides: DraftOverrides
): ChannelValues {
  const draft = overrides[item.notification_type]
  return Object.fromEntries(
    rowChannels(item).map((c) => [c, draft?.[c] ?? item[c]])
  ) as ChannelValues
}

// Writes a patch into the draft. A value set back to what is saved stops being
// a draft value, so the next read from the server reaches it again.
export function applyPatch(
  overrides: DraftOverrides,
  patch: RowValues,
  saved: RowValues
): DraftOverrides {
  const next: DraftOverrides = { ...overrides }
  Object.entries(patch).forEach(([key, values]) => {
    const type = Number(key)
    const row: ChannelValues = { ...next[type] }
    ;(Object.entries(values) as [Channel, boolean | undefined][]).forEach(
      ([channel, value]) => {
        if (value === undefined) return
        if (saved[type]?.[channel] === value) delete row[channel]
        else row[channel] = value
      }
    )
    if (Object.keys(row).length) next[type] = row
    else delete next[type]
  })
  return next
}

export function changeCount(overrides: DraftOverrides, saved: RowValues) {
  return Object.entries(overrides).reduce(
    (count, [key, row]) =>
      count +
      (Object.entries(row) as [Channel, boolean][]).filter(
        ([channel, value]) => saved[Number(key)]?.[channel] !== value
      ).length,
    0
  )
}

// Drops draft values the server now holds, and values for types the server no
// longer lists for this user.
export function pruneOverrides(
  overrides: DraftOverrides,
  saved: RowValues
): DraftOverrides {
  return applyPatch(
    {},
    Object.fromEntries(
      Object.entries(overrides).filter(([key]) => saved[Number(key)])
    ),
    saved
  )
}

// Every listed row with every channel it carries — the save sends full rows,
// as the settings endpoint requires.
export function submittedValues(
  settings: NotificationSettings,
  overrides: DraftOverrides
): Record<number, ChannelValues> {
  const values: Record<number, ChannelValues> = {}
  Object.values(settings)
    .flat()
    .forEach((item) => {
      values[item.notification_type] ??= currentRow(item, overrides)
    })
  return values
}

export function setAllPatch(
  items: NotificationSettingsItem[],
  rows: RowValues,
  next: boolean,
  isCriticalGroup: boolean,
  smsLock: SmsLock
): RowValues {
  const patch: RowValues = {}
  items.forEach((it) => {
    const channels = editableChannels(it, smsLock)
    patch[it.notification_type] = next
      ? (Object.fromEntries(channels.map((c) => [c, true])) as ChannelValues)
      : clearedRow(rows[it.notification_type], channels, isCriticalGroup)
  })
  return patch
}

export function setChannelPatch(
  items: NotificationSettingsItem[],
  rows: RowValues,
  channel: Channel,
  next: boolean,
  isCriticalGroup: boolean
): RowValues {
  const patch: RowValues = {}
  items
    .filter((it) => rowChannels(it).includes(channel))
    .forEach((it) => {
      const row = rows[it.notification_type]
      patch[it.notification_type] = {
        [channel]: next
          ? true
          : clearedChannel(row, channel, rowChannels(it), isCriticalGroup)
      }
    })
  return patch
}

export type CheckState = "checked" | "unchecked" | "mixed"

function checkState(values: boolean[]): CheckState {
  if (values.length && values.every(Boolean)) return "checked"
  return values.some(Boolean) ? "mixed" : "unchecked"
}

export function channelState(
  items: NotificationSettingsItem[],
  rows: RowValues,
  channel: Channel
): CheckState {
  return checkState(
    items
      .filter((it) => rowChannels(it).includes(channel))
      .map((it) => rows[it.notification_type][channel] === true)
  )
}

export function categoryState(
  items: NotificationSettingsItem[],
  rows: RowValues,
  smsLock: SmsLock
): CheckState {
  return checkState(
    items.flatMap((it) =>
      editableChannels(it, smsLock).map(
        (c) => rows[it.notification_type][c] === true
      )
    )
  )
}

const PROJECT_MESSAGE_TYPES = new Set([800, 801, 802])
const PROJECT_DOCUMENT_TYPES = new Set([17, 54, 55, 56, 57, 58, 59, 62])

// Headings that break a long category into readable runs. They only arrange
// rows on screen; every type keeps its own id, row and saved values.
const SUBGROUPS: Record<string, (type: number) => string> = {
  PROJECTS: (type) =>
    PROJECT_MESSAGE_TYPES.has(type)
      ? "messages"
      : PROJECT_DOCUMENT_TYPES.has(type)
        ? "documents"
        : "updates"
}

export interface RowSection {
  key: string
  items: NotificationSettingsItem[]
}

export function sectionsOf(
  groupKey: string,
  items: NotificationSettingsItem[]
): RowSection[] {
  const subgroupOf = SUBGROUPS[groupKey]
  if (!subgroupOf) return [{ key: "", items }]
  const sections: RowSection[] = []
  items.forEach((item) => {
    const key = subgroupOf(item.notification_type)
    const section = sections.find((s) => s.key === key)
    if (section) section.items.push(item)
    else sections.push({ key, items: [item] })
  })
  return sections.length > 1 ? sections : [{ key: "", items }]
}

export function searchSettings(
  settings: NotificationSettings,
  query: string
): [string, NotificationSettingsItem[]][] {
  const needle = query.trim().toLocaleLowerCase()
  if (!needle) return []
  return Object.entries(settings)
    .map(
      ([key, items]) =>
        [
          key,
          items.filter((it) =>
            it.notification_type_name.toLocaleLowerCase().includes(needle)
          )
        ] as [string, NotificationSettingsItem[]]
    )
    .filter(([, items]) => items.length > 0)
}
